"use client"

import { memo } from "react"
import Link from "next/link"
import { useTranslation } from "@/components/language-provider"
import { SITE_CONFIG } from "@/lib/constants"

const Footer = memo(function Footer() {
  const { t, locale } = useTranslation()

  const academyLinks = [
    { label: t("nav.courses"), href: "/egitimler/" },
    { label: t("nav.instructors"), href: "/egitmenler/" },
    { label: t("nav.becomeInstructor"), href: "/egitmen-ol/" },
  ]

  const ecosystemLinks = [
    { label: "glossgo.com", href: SITE_CONFIG.mainSite },
    { label: "compare", href: SITE_CONFIG.compareSite },
    { label: "partner", href: SITE_CONFIG.partnerSite },
    { label: t("footer.about"), href: `${SITE_CONFIG.mainSite}/${locale}/hakkimizda` },
  ]

  const legalLinks = [
    { label: t("footer.privacy"), href: "/legal/" },
    { label: t("footer.terms"), href: "/legal/" },
  ]

  return (
    <footer className="bg-gray-100 border-t border-gray-200 py-12 px-4" data-testid="beauty-academy-footer">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4">
              <img src="/images/glossgo-logo.svg" alt="glossgo" className="h-9 w-auto" />
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">{t("footer.tagline")}</p>
          </div>

          {/* Academy */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">{t("footer.academy")}</h3>
            <ul className="space-y-2.5" data-testid="beauty-academy-footer-academy-links">
              {academyLinks.map((link) => (
                <li key={link.href + link.label}>
                  <Link
                    href={link.href}
                    data-testid={`beauty-academy-footer-academy-link-row-${link.href}`}
                    className="text-gray-600 hover:text-purple-600 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Ecosystem */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">{t("footer.ecosystem")}</h3>
            <ul className="space-y-2.5" data-testid="beauty-academy-footer-ecosystem-links">
              {ecosystemLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`beauty-academy-footer-ecosystem-link-row-${link.href}`}
                    className="text-gray-600 hover:text-purple-600 transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">{t("footer.legal")}</h3>
            <ul className="space-y-2.5" data-testid="beauty-academy-footer-legal-links">
              {legalLinks.map((link, i) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    data-testid={`beauty-academy-footer-legal-link-row-${i}`}
                    className="text-gray-600 hover:text-purple-600 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 text-center">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} glossgo. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  )
})

export default Footer
